import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Pencil, Printer, Paperclip, Eye, FileText, History as HistoryIcon } from "lucide-react";
import { STATUS_BADGE, formatCurrency, formatNumber } from "@/lib/constants";
import { toast } from "sonner";

export default function RecordViewPage() {
  const { moduleKey, recordId } = useParams();
  const { perms } = useAuth();
  const perm = (perms && perms[moduleKey]) || { read: true, create: true, update: true, delete: true };
  const canUpdate = perm.update === true || (Array.isArray(perm.update) && perm.update.length > 0);
  const [meta, setMeta] = useState(null);
  const [record, setRecord] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([
      api.get("/meta/modules"),
      api.get(`/modules/${moduleKey}/records/${recordId}`),
      api.get(`/modules/${moduleKey}/records/${recordId}/history`).catch(() => ({ data: { history: [] } })),
    ])
      .then(([m, r, h]) => {
        if (!mounted) return;
        const found = (m.data.modules || []).find((x) => x.key === moduleKey);
        setMeta(found);
        setRecord(r.data);
        setHistory(h.data?.history || []);
      })
      .catch((e) => {
        if (!mounted) return;
        setError(e.response?.data?.detail || "Failed to load record");
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [moduleKey, recordId]);

  const goEdit = () => {
    // Full-page edit form in a new tab (no drawer) so every field is visible.
    window.open(`/m/${moduleKey}/${recordId}/edit`, "_blank", "noopener");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Record link copied");
    } catch {
      toast.error("Copy failed — copy the URL from the address bar");
    }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-4">
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6 text-sm text-red-700">
            {error}. <Link to={`/m/${moduleKey}`} className="underline font-semibold">Back to list</Link>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (!meta || !record) return null;

  const title = record.data?.[meta.title_field] || "Record Details";

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 print:p-0" data-testid="record-view-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-3 flex-wrap print:hidden">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">{meta.group} · {meta.label}</div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1" data-testid="record-title">{title}</h1>
          <p className="text-sm text-slate-500 mt-1">
            Full record view · all {meta.fields.length} fields
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={copyLink} data-testid="copy-record-link">
            <FileText className="h-4 w-4 mr-2" /> Copy Link
          </Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="print-record">
            <Printer className="h-4 w-4 mr-2" /> Print
          </Button>
          <Link to={`/m/${moduleKey}`}>
            <Button variant="outline" data-testid="back-to-list">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to {meta.label}
            </Button>
          </Link>
          {canUpdate && (
            <Button onClick={goEdit} className="bg-slate-900 hover:bg-slate-800" data-testid="edit-record">
              <Pencil className="h-4 w-4 mr-2" /> Edit
            </Button>
          )}
        </div>
      </div>

      {/* Print-only header */}
      <div className="hidden print:block mb-4">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">HARDIK MEDITECH — {meta.label}</div>
        <h1 className="text-2xl font-bold text-slate-900 mt-1">{title}</h1>
      </div>

      {/* Meta strip */}
      <Card className="border-slate-200" data-testid="record-meta-card">
        <CardContent className="p-5 grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Created</div>
            <div className="text-slate-800">{record.created_at ? new Date(record.created_at).toLocaleString() : "—"}</div>
            <div className="text-[11px] text-slate-500 truncate">by {record.created_by_name || record.created_by || "—"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Last Updated</div>
            <div className="text-slate-800">{record.updated_at ? new Date(record.updated_at).toLocaleString() : "—"}</div>
            <div className="text-[11px] text-slate-500 truncate">by {record.updated_by_name || record.updated_by || "—"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Record ID</div>
            <div className="font-mono text-[11px] text-slate-500 break-all">{record.id}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Status</div>
            <ViewValue value={record.data?.status} field={{ type: "status" }} />
          </div>
        </CardContent>
      </Card>

      {/* All fields */}
      <Card className="border-slate-200">
        <CardContent className="p-5 sm:p-6">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-5">
            Record Details · {meta.fields.length} fields
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            {meta.fields.map((f) => (
              <div
                key={f.key}
                className={(f.type === "textarea" || f.type === "attachments") ? "md:col-span-2" : ""}
                data-testid={`view-field-${f.key}`}
              >
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
                  {f.label}
                  {f.required && <span className="text-red-500 ml-0.5">*</span>}
                </div>
                <ViewValue value={record.data?.[f.key]} field={f} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Activity history */}
      {history.length > 0 && (
        <Card className="border-slate-200 print:hidden" data-testid="record-history-card">
          <CardContent className="p-5 sm:p-6">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold mb-4">
              <HistoryIcon className="h-3.5 w-3.5" /> Activity History · {history.length} entries
            </div>
            <div className="space-y-3">
              {history.map((h) => (
                <div key={h._id} className="border-l-2 border-slate-200 pl-3">
                  <div className="text-xs font-semibold text-slate-700 capitalize">{h.action?.replace(":", " · ")}</div>
                  <div className="text-xs text-slate-500">
                    {h.by_name || h.by_email} · {new Date(h.at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ViewValue({ value, field }) {
  if (value == null || value === "" || (Array.isArray(value) && value.length === 0)) {
    return <div className="text-sm text-slate-400">—</div>;
  }
  if (field.type === "status") {
    return <Badge variant="outline" className={STATUS_BADGE(value)}>{value}</Badge>;
  }
  if (field.type === "currency") {
    return <div className="text-sm font-semibold text-slate-900 font-mono">{formatCurrency(value)}</div>;
  }
  if (field.type === "number") {
    return <div className="text-sm font-semibold text-slate-900 font-mono">{formatNumber(value)}</div>;
  }
  if (field.type === "date") {
    return <div className="text-sm text-slate-800">{value}</div>;
  }
  if (field.type === "textarea") {
    return (
      <div className="text-sm text-slate-800 whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded-md p-3">
        {value}
      </div>
    );
  }
  if (field.type === "attachments") {
    const files = Array.isArray(value) ? value : [];
    if (files.length === 0) return <div className="text-sm text-slate-400">—</div>;
    // Session cookie handles auth (same-origin /api ingress) — no tokens in URLs.
    return (
      <div className="space-y-1.5">
        {files.map((f, i) => {
          const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${f.id}`;
          return (
            <a
              key={f.id || `${f.filename || "file"}-${i}`}
              href={url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-xs hover:bg-slate-100"
              data-testid={`view-attachment-${i}`}
            >
              <Paperclip className="h-3.5 w-3.5 text-slate-500 flex-shrink-0" />
              <span className="flex-1 truncate text-slate-700">
                {f.filename || f.original_filename || f.id}
              </span>
              <Eye className="h-3.5 w-3.5 text-blue-600" />
            </a>
          );
        })}
      </div>
    );
  }
  return <div className="text-sm text-slate-800 whitespace-pre-wrap break-words">{String(value)}</div>;
}
